import React, { useState } from "react";
import "./documentUpload.css";
import { downloadData, uploadData } from "../../services/FileUploadService";

interface DocumentUploadProps {
  isOpen: boolean;
  onClose: () => void;
}

const DocumentUpload: React.FC<DocumentUploadProps> = ({ isOpen, onClose }) => {
  const [uploadedFiles, setUploadedFiles] = useState<{
    [key: string]: File | null;
  }>({
    wasteData: null,
    wasteCategoryData: null,
    transportationData: null,
    fuelData: null,
    prodDB: null,
    technologyData: null,
    mcdmDB: null,
    counties: null,
  });

  const [isDownloading, setIsDownloading] = useState<{
    [key: string]: boolean;
  }>({});
  
  const [isUploading, setIsUploading] = useState(false);

  const uploadCategories = [
    { key: "wasteData", title: "Waste Data" },
    { key: "wasteCategoryData", title: "Waste Category Data" },
    { key: "transportationData", title: "Transportation Data" },
    { key: "fuelData", title: "Fuel Data" },
    { key: "prodDB", title: "Prod DB" },
    { key: "technologyData", title: "Technology Data" },
    { key: "mcdmDB", title: "MCDM DB" },
    { key: "countries", title: "Countries" },
  ];
  
  const handleFileUpload = (categoryKey: string, file: File) => {
    setUploadedFiles((prev) => ({
      ...prev,
      [categoryKey]: file,
    }));
  };

  const handleDownloadTemplate = async (categoryKey: string) => {
    try {
      setIsDownloading((prev) => ({ ...prev, [categoryKey]: true }));
      
      const response = await downloadData(categoryKey);
      
      const blob = new Blob([response.data], { type: "text/csv" });
      
      const url = window.URL.createObjectURL(blob);
      
      const link = document.createElement("a");
      link.href = url;
      link.download = `${categoryKey}.csv`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      window.URL.revokeObjectURL(url);
      
      console.log(`Downloaded template for ${categoryKey}`);
    } catch (error) {
      console.error(`Error downloading template for ${categoryKey}:`, error);
      alert(`Failed to download template for ${categoryKey}. Please try again.`);
    } finally {
      setIsDownloading((prev) => ({ ...prev, [categoryKey]: false }));
    }
  };

  const handleApply = async () => {
    console.log("Apply uploads:", uploadedFiles);
    
    setIsUploading(true);
    
    try {
      // Crea un array di promesse per tutti gli upload
      const uploadPromises = Object.entries(uploadedFiles)
        .filter(([_, file]) => file !== null)
        .map(async ([categoryKey, file]) => {
          if (!file) return;
          
          const formData = new FormData();
          formData.append("file", file);
          
          console.log(`Uploading ${categoryKey}: ${file.name}`);
          
          try {
            const response = await uploadData(categoryKey, formData);
            console.log(`Upload successful for ${categoryKey}:`, response);
            return { categoryKey, success: true, response };
          } catch (error) {
            console.error(`Upload failed for ${categoryKey}:`, error);
            return { categoryKey, success: false, error };
          }
        });
      

      const results = await Promise.all(uploadPromises);
      

      const failedUploads = results.filter(result => !result?.success);
      
      if (failedUploads.length > 0) {
        alert(`Some uploads failed: ${failedUploads.map(r => r.categoryKey).join(", ")}`);
      } else {
        setUploadedFiles({
          wasteData: null,
          wasteCategoryData: null,
          transportationData: null,
          fuelData: null,
          prodDB: null,
          technologyData: null,
          mcdmDB: null,
          countries: null,
        });
      }
      
    } catch (error) {
      console.error("Error during upload process:", error);
      alert("An error occurred during the upload process.");
    } finally {
      setIsUploading(false);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="document-upload-overlay" onClick={onClose}>
      <div className="document-upload-wrapper">
        <button className="document-upload-close" onClick={onClose}>
          ×
        </button>
        <div
          className="document-upload-modal"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="document-upload-content">
            <h2 className="document-upload-title">Document Upload</h2>
            <p className="document-upload-description">
              Download the data sheet first, and then upload your
              modified file.
            </p>

            <div className="document-upload-grid">
              {uploadCategories.map((category) => (
                <div key={category.key} className="document-upload-category">
                  <h3 className="document-upload-category-title">
                    {category.title}
                  </h3>

                  <div className="document-upload-dropzone">
                    <input
                      type="file"
                      accept=".csv"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleFileUpload(category.key, file);
                      }}
                      className="document-upload-input"
                      id={`upload-${category.key}`}
                    />
                    <label
                      htmlFor={`upload-${category.key}`}
                      className="document-upload-label"
                    >
                      {uploadedFiles[category.key] ? (
                        <>
                          <span className="document-upload-checkmark">✓</span>
                          {uploadedFiles[category.key]!.name}
                        </>
                      ) : (
                        "Drag & drop a CSV file here, or click to select from your computer"
                      )}
                    </label>
                  </div>

                  <div className="document-upload-buttons">
                    <button
                      className="document-upload-download-btn"
                      onClick={() => handleDownloadTemplate(category.key)}
                      disabled={isDownloading[category.key]}
                    >
                      {isDownloading[category.key] ? "Downloading..." : "⭳ Download CSV"}
                    </button>
                    <button
                      className="document-upload-upload-btn"
                      onClick={() =>
                        document
                          .getElementById(`upload-${category.key}`)
                          ?.click()
                      }
                    >
                      ⭱ Upload CSV
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="document-upload-actions">
              <button
                className="document-upload-apply-btn"
                onClick={handleApply}
                disabled={isUploading || Object.values(uploadedFiles).every(file => file === null)}
              >
                {isUploading ? "Uploading..." : "Apply →"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentUpload;
